// dashboard/frontend/src/components/RuleDrillPanel.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import CohortBacktestSection from './CohortBacktestSection';
import EntityConfigEditor from './EntityConfigEditor';

/* Drill-down d'une règle minée (#3a) : détail prédicat + métriques + backtest cohorte
   + (Lot 2) config sortie TP/SL de l'activation + trades du bot via cette règle.
   <style> embarqué => portable hors DiscoveryPage (ex: badge 📏 dans /positions). */

const CSS = `
.tdp-overlay{position:fixed;inset:0;background:rgba(0,0,0,.66);backdrop-filter:blur(2px);z-index:200;display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:24px;font-family:'JetBrains Mono',ui-monospace,monospace;color:var(--text);}
.tdp-panel{background:var(--bg);border:1px solid var(--border);border-radius:14px;width:100%;max-width:min(1200px,95vw);max-height:90vh;overflow-y:auto;padding:20px;box-shadow:0 24px 70px rgba(0,0,0,.6);}
.tdp-head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;}
.tdp-title{font-size:15px;font-weight:700;}
.tdp-sub{font-size:11px;color:var(--text2);}
.tdp-ctrl{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:8px 0;}
.tdp-ctrl .lbl{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-right:2px;}
.tdp-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 10px;border-radius:6px;cursor:pointer;letter-spacing:.03em;}
.tdp-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.tdp-btn:disabled{opacity:.45;cursor:not-allowed;}
/* Classes du backtest cohorte (BacktestMatrixView/CohortBacktestSection) — dupliquées
   depuis DiscoveryPage pour rendre ce panneau autonome hors /discovery (ex: badge 📏 /positions). */
.tdp-empty{padding:24px;text-align:center;color:var(--text2);font-size:12px;}
.tdp-bt-card{margin-top:12px;border:1px solid var(--border);border-radius:8px;padding:12px;background:var(--surface);}
.tdp-bt-hd{font-size:13px;font-weight:600;margin-bottom:8px;color:var(--text);}
.tdp-score{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:14px;}
@media(max-width:680px){.tdp-score{grid-template-columns:repeat(3,1fr);}}
.tdp-kpi{background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:9px 10px;}
.tdp-kpi .k{font-size:9px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;}
.tdp-kpi .v{font-size:16px;font-weight:800;margin-top:3px;}
.tdp-tbl{width:100%;border-collapse:collapse;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.tdp-tbl th{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 10px;background:#0e1014;}
.tdp-tbl th.num{text-align:right;}
.tdp-tbl td{padding:8px 10px;font-size:11.5px;border-top:1px solid #16191f;}
.tdp-tbl td.num{text-align:right;font-variant-numeric:tabular-nums;}
.tdp-mint{font-size:10px;color:var(--text2);cursor:pointer;}
.tdp-tl{font-size:9px;font-weight:700;text-decoration:none;padding:2px 5px;border-radius:4px;margin-right:4px;}
.tdp-tl.sol{color:#9bd1ff;} .tdp-tl.gm{color:var(--amber);} .tdp-tl.dx{color:var(--green);}
.tdp-pos{color:var(--green);} .tdp-neg{color:var(--red);} .tdp-muted{color:var(--text2);}
.tdp-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:5px;}
.tdp-badge.rug{background:rgba(255,59,92,.16);color:var(--red);}
`;

const SOLSCAN = (x) => `https://solscan.io/account/${x}`;
const GMGN_TOKEN = (x) => `https://gmgn.ai/sol/token/${x}`;
const DEXSCREENER = (x) => `https://dexscreener.com/solana/${x}`;
const trunc = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '—');
const fmtPeak = (v) => v == null ? <span style={{ color: 'var(--text2,#8a8f98)' }}>—</span>
  : <span style={{ color: v >= 0 ? 'var(--green,#00e08a)' : 'var(--red,#ff3b5c)' }}>{v >= 0 ? '+' : ''}{Number(v).toFixed(1)}%</span>;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export default function RuleDrillPanel({ rule, onClose }) {
  // Match l'activation correspondante (feature+op+threshold+slot_tag) : la config sortie
  // et l'armed_activation_id sont portés par discovery_rule_activations, pas par la règle minée.
  const { data: actData } = useQuery({
    queryKey: ['rule-activations-for-drill'],
    queryFn: () => api.get('/discovery/rules/activations').then(r => r.data),
    staleTime: 15000,
    enabled: !!rule,
  });

  const activation = React.useMemo(() => {
    if (!rule || !actData?.activations) return null;
    const st = (rule.slot_tag || '');
    return actData.activations.find(a =>
      a.feature === rule.feature && a.op === rule.op &&
      Number(a.threshold) === Number(rule.threshold) && (a.slot_tag || '') === st
    ) || null;
  }, [rule, actData]);

  const actId = activation?.id;

  const { data: botData, isLoading: botLoading } = useQuery({
    queryKey: ['rule-drill-bot', actId],
    queryFn: () => api.get('/positions/closed', {
      params: { armed_activation_id: `rule:${actId}`, limit: 200, shadow: 'all' },
    }).then(r => r.data),
    staleTime: 30000,
    enabled: !!actId,
  });
  const botPositions = botData?.positions || [];

  if (!rule) return null;
  const lc = rule.label_config || {};

  const initial = {
    __key: actId ? `rule:${actId}` : 'rule:none',
    max_pct_above_genesis: activation?.max_pct_above_genesis,
    max_progress: activation?.max_progress,
    max_token_age_sec: activation?.max_token_age_sec,
    crash_brutal_velocity_threshold: activation?.crash_brutal_velocity_threshold,
    crash_brutal_grace_sec: activation?.crash_brutal_grace_sec,
    tp_take_pct: activation?.tp_take_pct,
    trailing_pct: activation?.trailing_pct,
  };
  const saveRuleConfig = async (values) => {
    await api.put(`/discovery/rules/activation/${actId}/config`, values);
  };

  return (
    <div className="tdp-overlay" onClick={onClose}>
      <style>{CSS}</style>
      <div className="tdp-panel" onClick={(e) => e.stopPropagation()}>
        <div className="tdp-head">
          <div>
            <div className="tdp-title">
              {rule.feature} {rule.op} {rule.threshold}
            </div>
            <div className="tdp-sub">
              slot_tag : {rule.slot_tag || '—'} · label : peak≥{lc.peak_threshold_pct}% / {lc.basis}
              {lc.rug_excluded ? ' / rug exclu' : ''}
              {activation ? ` · activation #${activation.id} (${activation.mode})` : ' · non armée'}
            </div>
          </div>
          <button className="tdp-btn" onClick={onClose}>✕</button>
        </div>

        <div className="tdp-ctrl">
          <span className="lbl">lift OOS</span><b>{fmt(rule.lift_oos)}</b>
          <span className="lbl">lift train</span>{fmt(rule.lift_train)}
          <span className="lbl">support train</span>{rule.support_train ?? '—'}
          <span className="lbl">support OOS</span>{rule.support_oos ?? '—'}
        </div>
        <div className="tdp-sub" style={{ color: 'var(--amber,#ffb020)', marginBottom: 8 }}>
          Lift OOS = signal relatif (held-out) ; le verdict PnL exécutable vient du backtest cohorte ci-dessous.
        </div>

        {/* Config sortie (TP/SL) — uniquement si la règle est activée (porte armed_activation_id) */}
        {actId ? (
          <EntityConfigEditor
            initial={initial}
            showAge={true}
            onSave={saveRuleConfig}
            kind="rule"
            address={actId ? String(actId) : undefined}
            label={`Config entrée + sortie (TP/SL) — règle #${actId}`}
          />
        ) : (
          <div className="tdp-sub" style={{ margin: '10px 0', padding: 10, border: '1px dashed var(--border,#1f2329)', borderRadius: 8 }}>
            ⚙ Pour configurer l'entrée/sortie de cette règle, arme-la d'abord (bouton « Armer » / ArmingConsole) — la config sortie est portée par l'activation.
          </div>
        )}

        {/* Trades du bot via cette règle */}
        {actId && (
          <div style={{ marginTop: 18 }}>
            <div className="tdp-sub" style={{ fontWeight: 700, marginBottom: 6 }}>
              Tradé par le bot ({botLoading ? '…' : botPositions.length})
            </div>
            {botLoading ? <div className="tdp-sub">Chargement…</div>
              : botPositions.length === 0 ? <div className="tdp-sub">Aucun trade du bot via cette règle.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr style={{ color: 'var(--text2,#8a8f98)', fontSize: 9, textTransform: 'uppercase' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Token</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>PnL%</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Peak exit</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }} title="Prix à la décision vs launch">Entrée /launch</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Fermé</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Liens</th>
                  </tr></thead>
                  <tbody>
                    {botPositions.map(p => (
                      <tr key={p.id} style={{ borderTop: '1px solid #16191f' }}>
                        <td style={{ padding: '6px 8px' }} title={p.ui_mint || p.token_address}>{trunc(p.ui_mint || p.token_address)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtPeak(p.pnl_percent)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtPeak(p.exit_peak_pnl)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.dec_pct_above_genesis == null ? <span style={{ color: 'var(--text2,#8a8f98)' }}>—</span> : <span style={{ color: p.dec_pct_above_genesis * 100 >= 100 ? 'var(--red,#ff3b5c)' : 'var(--text2,#8a8f98)' }}>{p.dec_pct_above_genesis >= 0 ? '+' : ''}{Math.round(p.dec_pct_above_genesis * 100)}%</span>}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10 }}>{fmtDate(p.closed_at)}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <a href={SOLSCAN(p.token_address)} target="_blank" rel="noopener noreferrer" style={{ color: '#9bd1ff', marginRight: 6 }}>SS</a>
                          {p.ui_mint && <a href={GMGN_TOKEN(p.ui_mint)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--amber,#ffb020)', marginRight: 6 }}>GM</a>}
                          {p.ui_mint && <a href={DEXSCREENER(p.ui_mint)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green,#00e08a)' }}>DEX</a>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        )}

        <CohortBacktestSection ruleId={rule.id} slotTag={rule.slot_tag} />
      </div>
    </div>
  );
}

function fmt(v) {
  return (v === null || v === undefined) ? '—' : Number(v).toFixed(2);
}
